/**
 * An `AbortSignal` that fires after `timeoutMs`, or `undefined` when the deadline is
 * disabled (`<= 0`) or the runtime lacks `AbortSignal.timeout`. `typeof` on the bare
 * global is the only safe probe: optional chaining still throws on an undeclared name.
 */
export function abortSignalAfter(timeoutMs: number): AbortSignal | undefined {
  if (timeoutMs <= 0) return undefined;
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return undefined;
  return AbortSignal.timeout(timeoutMs);
}

/** Waits with caller cancellation without cancelling work shared by other callers. */
export function awaitWithSignal<T>(work: Promise<T>, signal?: AbortSignal | null): Promise<T> {
  if (!signal) return work;
  return new Promise((resolve, reject) => {
    const cleanup = () => signal.removeEventListener('abort', aborted);
    const aborted = () => {
      cleanup();
      reject(signal.reason ?? new DOMException('Request aborted', 'AbortError'));
    };
    work.then((value) => { cleanup(); resolve(value); }, (error: unknown) => { cleanup(); reject(error); });
    if (signal.aborted) aborted();
    else signal.addEventListener('abort', aborted, { once: true });
  });
}

/** Bounds a complete HTTP operation, including body consumption, and releases its timer. */
export async function withRequestDeadline<T>(request: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T> {
  // Own the timer so completed requests release it, including on older WebViews
  // without AbortSignal.timeout. Rejection matches the existing fetch boundary.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);
  try {
    return await awaitWithSignal(request(controller.signal), controller.signal);
  } finally {
    clearTimeout(timer);
  }
}
